import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../lib/supabase';
import { SUPABASE_CONFIG } from '../config/supabase-config';
import { createScopedLogger } from '@/utils/logger.js';

/**
 * Google Authentication Component
 * Provides easy Google OAuth sign-in/sign-out for all users
 * Serves valets, clients, and admins accessing odv-truest platform
 */

const log = createScopedLogger('GoogleAuth');

export default function GoogleAuth({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(isOpen !== undefined ? isOpen : true);
  const [justLoggedIn, setJustLoggedIn] = useState(false);

  // Sync visibility with isOpen prop
  useEffect(() => {
    
if (isOpen !== undefined) {
      setIsVisible(isOpen);
    }
  }, [isOpen]);

  useEffect(() => {
    // Listen for auth state changes
    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      const currentUser = session?.user;

      // No domain validation needed - platform serves valets, clients, and admins
      // All authenticated users are allowed
      if (currentUser && event === 'SIGNED_IN') {
        log.debug?.('User signed in', { email: currentUser.email, provider: currentUser.app_metadata?.provider });
      }

      setUser(currentUser);
      setLoading(false);

      if (currentUser) {
        // Note: Account linking is handled by auth.jsx to avoid race conditions
        // Only auto-close if this is a fresh login, not when already logged in
        if (justLoggedIn) {
          setTimeout(() => {
            setIsVisible(false);
            if (onClose) onClose();
            setJustLoggedIn(false);
          }, 2000); // Give user 2 seconds to see they're logged in
        }
      }
    });

    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, [onClose, justLoggedIn]);


const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    setJustLoggedIn(true);

    try {
      log.debug?.('Starting Google OAuth', {
        redirectUrl: SUPABASE_CONFIG.redirectUrl,
        origin: window.location.origin
      });

      // Use Supabase Google OAuth
      // Append /auth to redirect URL for OAuth callback handling
      const redirectTo = SUPABASE_CONFIG.redirectUrl.endsWith('/auth') 
        ? SUPABASE_CONFIG.redirectUrl 
        : `${SUPABASE_CONFIG.redirectUrl}/auth`;
      
      const { error } = await auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo,
          queryParams: {
            prompt: 'select_account' // Allow account selection, no domain restriction
          }
        }
      });

      if (error) {
        log.error?.('OAuth error', error);
        throw error;
      }

      log.debug?.('OAuth initiated successfully', { provider: 'google' });

    } catch (error) {
      log.error?.('Sign-in error', error);
      setError(error.message || 'Authentication error');
      setJustLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  
const handleSignOut = () => {
    log.debug?.('Starting sign out');

    // Clear all auth-related storage immediately
    localStorage.removeItem('odv-supabase-auth');
    localStorage.removeItem('sb-bmmfmmdpphbndezgugtx-auth-token');
    sessionStorage.clear();

    // Clear local state
    setUser(null);

    // Close the modal
    setIsVisible(false);
    if (onClose) onClose();

    // Navigate to auth page (not landing page)
    navigate('/auth');

    log.debug?.('Successfully signed out');
  };

  // Don't render if hidden
  if (!isVisible) return null;

  // Floating auth widget style

const containerStyle = {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 9999,
    backgroundColor: '#1a1a1a',
    padding: '24px',
    borderRadius: '16px',
    border: '2px solid #AEA36D',
    boxShadow: '0 8px 32px rgba(174, 163, 109, 0.4)',
    minWidth: '340px',
    maxWidth: '400px'
  };

  
const closeButtonStyle = {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: 'transparent',
    border: 'none',
    color: '#888',
    cursor: 'pointer',
    padding: '4px',
    fontSize: '20px',
    lineHeight: '1',
    transition: 'color 0.2s',
    zIndex: 10000
  };

  
const buttonStyle = {
    width: '100%',
    padding: '10px 16px',
    backgroundColor: '#AEA36D',
    color: '#000',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'background-color 0.2s'
  };

  
const googleIconSvg = (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );

  return (
    <div style={containerStyle}>
      {/* Close button */}
      <button 
        onClick={() => {
          setIsVisible(false);
          if (onClose) onClose();
        }}
        style={closeButtonStyle}
        onMouseEnter={(e) => e.target.style.color = '#AEA36D'}
        onMouseLeave={(e) => e.target.style.color = '#888'}
        title="Close"
      >
        ×
      </button>
      
      {/* Header Section */}
      <div style={{
        marginBottom: '20px',
        borderBottom: '1px solid rgba(174, 163, 109, 0.3)',
        paddingBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '8px'
      }}>
        {/* ODV Logo - Dominant and Even Bigger */}
        <img
          src="/images/odv-spelled.png"
          alt="ODV - Open Door Valet"
          style={{
            height: '64px',
            width: 'auto',
            filter: 'brightness(1.1)'
          }}
        />
        {/* GSeat Pro Text - Smaller with gold/bronze shiny gradient */}
        <h2 style={{
          fontSize: '9px',
          fontWeight: '700',
          margin: '0',
          letterSpacing: '1.2px',
          textTransform: 'uppercase',
          background: 'linear-gradient(135deg, #E5C88A 0%, #C4A969 25%, #AEA36D 50%, #9B8357 75%, #8B6340 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6)) drop-shadow(0 0 8px rgba(229,200,138,0.4))',
          animation: 'bronzeShine 3s ease-in-out infinite'
        }}>
          <style>
            {`
              @keyframes bronzeShine {
                0%, 100% {
                  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6)) drop-shadow(0 0 8px rgba(229,200,138,0.4)) brightness(1);
                }
                50% {
                  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6)) drop-shadow(0 0 12px rgba(229,200,138,0.6)) brightness(1.15);
                }
              }` }
          </style>
          GSEAT PRO
        </h2>
      </div>
      
      {loading && !user ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>
          Loading..
        </div>
      ) : user ? (
        <div>
          {/* User Info Section with Profile Picture */}
          <div style={{ 
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            backgroundColor: 'rgba(174, 163, 109, 0.1)',
            borderRadius: '8px'
          }}>
            {/* Profile Picture with fallback */}
            <div style={{ position: 'relative' }}>
              {user.photoURL ? (
                <img 
                  src={user.photoURL}
                  alt={user.displayName || 'Profile'}
                  referrerPolicy="no-referrer"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    border: '2px solid #AEA36D',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    log.warn?.('Failed to load profile image', { url: user.photoURL });
                    e.target.style.display = 'none';
                    
const fallback = e.target.nextElementSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  border: '2px solid #AEA36D',
                  backgroundColor: '#AEA36D',
                  display: user.photoURL ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#000'
                }}
              >
                {user.displayName ? user.displayName[0].toUpperCase() : user.email[0].toUpperCase()}
              </div>
            </div>
            {/* User Details */}
            <div style={{ flex: 1 }}>
              <div style={{ color: '#AEA36D', fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase' }}>
                Signed in as
              </div>
              {user.displayName && (
                <div style={{ color: '#fff', fontSize: '15px', fontWeight: '600', marginBottom: '2px' }}>
                  {user.displayName}
                </div>
              )}
              <div style={{ color: '#888', fontSize: '13px', wordBreak: 'break-all' }}>
                {user.email}
              </div>
            </div>
          </div>
          
          <button 
            onClick={handleSignOut}
            style={{...buttonStyle, backgroundColor: '#666' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#777'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#666'}
          >
            Sign Out
          </button>
        </div>
      ) : (
        <div>
          <button
            onClick={handleGoogleSignIn}
            style={buttonStyle}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#998D5D'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#AEA36D'}
          >
            {googleIconSvg}
            Sign in with Google
          </button>
          <div style={{
            marginTop: '8px',
            fontSize: '11px',
            color: '#888',
            textAlign: 'center'
          }}>
            Valets · Clients · Admins
          </div>
        </div>
      )}
      
      {error && (
        <div style={{ 
          marginTop: '12px',
          padding: '8px',
          backgroundColor: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: '4px',
          color: '#ff6b6b',
          fontSize: '12px'
        }}>
          {error}
        </div>
      )}
    </div>
  );
}